import * as React from "react";
import { List, type RowComponentProps } from "react-window";
import { Box, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const ROW_HEIGHT = 42;
const SEPARATOR_EXTRA = 38;
const MAX_HEIGHT = 220;

function Row({ index, style, items }: RowComponentProps<{ items: React.ReactNode[] }>) {
  return React.cloneElement(items[index] as React.ReactElement, { style });
}

interface VirtualizedListboxProps extends React.HTMLAttributes<HTMLElement> {
  searchText?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  onSearchBlur?: (e: React.FocusEvent) => void;
  separatorIndex?: number;
}

const VirtualizedListbox = React.forwardRef<HTMLDivElement, VirtualizedListboxProps>(
  function VirtualizedListbox(props, ref) {
    const { children, searchText, onSearchChange, searchPlaceholder, onSearchBlur, separatorIndex = -1, ...other } = props;
    const items = React.Children.toArray(children);
    const showSearch = onSearchChange !== undefined;

    const getRowHeight = React.useCallback(
      (index: number) =>
        separatorIndex >= 0 && index === separatorIndex
          ? ROW_HEIGHT + SEPARATOR_EXTRA
          : ROW_HEIGHT,
      [separatorIndex]
    );

    const totalHeight = separatorIndex >= 0
      ? items.length * ROW_HEIGHT + SEPARATOR_EXTRA
      : items.length * ROW_HEIGHT;
    const height = Math.min(totalHeight, MAX_HEIGHT);

    return (
      <div ref={ref} {...other} style={{ ...other.style, overflow: "hidden" }}>
        {showSearch && (
          <Box
            sx={{ p: 1, borderBottom: '1px solid #E5E7EB' }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <TextField
              size="small"
              fullWidth
              placeholder={searchPlaceholder ?? "Search..."}
              value={searchText ?? ''}
              onChange={(e) => onSearchChange!(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') {
                  e.stopPropagation();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#9CA3AF', fontSize: '18px' }} />
                  </InputAdornment>
                ),
              }}
              autoFocus
              onBlur={onSearchBlur}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '13px',
                  backgroundColor: '#F9FAFB',
                },
              }}
            />
          </Box>
        )}
        <List
          rowComponent={Row}
          rowCount={items.length}
          rowHeight={getRowHeight}
          rowProps={{ items }}
          overscanCount={5}
          style={{
            height,
            width: "100%",
            scrollbarWidth: "none",          /* Firefox */
            msOverflowStyle: "none",         /* IE/Edge */
          } as React.CSSProperties}
        />
      </div>
    );
  }
);

export default VirtualizedListbox;
